<template>
  <!-- 背景遮罩，只在展开状态时显示 -->
  <div v-if="visible && !isMinimized" class="player-overlay" @click="closeExpanded"></div>
  
  <transition name="floating-player">
    <div v-if="visible" class="floating-audio-player" :class="{ 'minimized': isMinimized }">
      <!-- 播放器头部 -->
      <div class="player-header" @click="toggleMinimize">
        <div class="track-info">
          <div class="track-cover-wrapper" :class="{ 'playing': isPlaying }">
            <img :src="IMAGE_BASE_URL + audioData.poster" :alt="audioData.zhName" class="track-cover">
          </div>
          <div class="track-details">
            <div class="track-title">{{ currentTrack?.title || audioData.zhName }}</div>
            <div class="track-artist">{{ audioData.artist || '未知艺术家' }}</div>
          </div>
        </div>
        
        <div class="player-actions">
          <button class="action-btn" @click.stop="togglePlay">
            <i :class="isPlaying ? 'icon-pause-small' : 'icon-play-small'"></i>
          </button>
          <button class="action-btn close-btn" @click.stop="closePlayer">
            <i class="icon-close"></i>
          </button>
        </div>
      </div>

      <!-- 展开的播放器内容 -->
      <div v-if="!isMinimized" class="player-content">
        <!-- 展开状态的封面 -->
        <div class="expanded-cover-section">
          <div class="expanded-cover-wrapper" :class="{ 'playing': isPlaying }">
            <img :src="IMAGE_BASE_URL + audioData.poster" :alt="audioData.zhName" class="expanded-cover">
          </div>
        </div>
        
        <!-- 进度条 -->
        <div class="progress-section">
          <div class="progress-bar" @click="seekTo" ref="progressBar">
            <div class="progress-background"></div>
            <div class="progress-current" :style="{ width: progressPercentage + '%' }"></div>
          </div>
          <div class="time-info">
            <span class="time-current">{{ currentTime }}</span>
            <span class="time-total">{{ totalTime }}</span>
          </div>
        </div>

        <!-- 控制按钮 -->
        <div class="control-buttons">
          <button class="control-btn" @click="playPrevious" :disabled="currentEpisodeIndex <= 0">
            <i class="icon-prev-small"></i>
          </button>
          <button class="control-btn play-btn-large" @click="togglePlay">
            <i :class="isPlaying ? 'icon-pause-medium' : 'icon-play-medium'"></i>
          </button>
          <button class="control-btn" @click="playNext" :disabled="currentEpisodeIndex >= (audioData.episodes?.length || 1) - 1">
            <i class="icon-next-small"></i>
          </button>
          <button class="control-btn" @click="openFullPlayer">
            <i class="icon-expand"></i>
          </button>
        </div>

        <!-- 音量控制 -->
        <div class="volume-section">
          <i class="icon-volume-small"></i>
          <div class="volume-bar" @click="setVolume" ref="volumeBar">
            <div class="volume-background"></div>
            <div class="volume-current" :style="{ width: (volume * 100) + '%' }"></div>
          </div>
        </div>
      </div>

      <!-- 注意：不再使用本地 audio 元素，使用全局音频管理器 -->
    </div>
  </transition>
</template>

<script>
import { getEpisodeList } from '../api';
import { IMAGE_BASE_URL } from '../utils/constants';
import audioManager from '../utils/audioManager';

export default {
  name: 'FloatingAudioPlayer',
  data() {
    // 从音频管理器获取初始状态
    const audioState = audioManager.getState();
    return {
      visible: false,
      isMinimized: true,
      ...audioState,
      IMAGE_BASE_URL,
      // 添加取消订阅函数的引用
      unsubscribeAudioManager: null
    };
  },
  computed: {
    currentTrack() {
      return this.audioData.episodes?.[this.currentEpisodeIndex];
    }
  },
  mounted() {
    // 订阅音频管理器状态变化
    this.unsubscribeAudioManager = audioManager.subscribe(this.handleAudioStateChange);

    // 监听来自AudioPlayer的事件
    window.addEventListener('showFloatingPlayer', this.handleShowPlayer);
    window.addEventListener('hideFloatingPlayer', this.handleHidePlayer);

    // 监听音频管理器的播放剧集事件
    window.addEventListener('audioManager:playEpisode', this.handlePlayEpisodeEvent);

    // 尝试恢复上次的播放状态
    this.tryRestoreLastSession();
  },
  beforeUnmount() {
    // 取消订阅音频管理器
    if (this.unsubscribeAudioManager) {
      this.unsubscribeAudioManager();
    }

    // 清理事件监听
    window.removeEventListener('showFloatingPlayer', this.handleShowPlayer);
    window.removeEventListener('hideFloatingPlayer', this.handleHidePlayer);
    window.removeEventListener('audioManager:playEpisode', this.handlePlayEpisodeEvent);
  },
  methods: {
    // 处理音频管理器状态变化
    handleAudioStateChange(newState) {
      // 批量更新状态，避免频繁的响应式更新
      const updates = {};
      Object.keys(newState).forEach(key => {
        if (Object.prototype.hasOwnProperty.call(this.$data, key)) {
          updates[key] = newState[key];
        }
      });
      
      // 使用Object.assign一次性更新所有状态
      Object.assign(this.$data, updates);
      
      // 在下一个tick后强制更新UI
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },

    // 处理播放剧集事件
    handlePlayEpisodeEvent(event) {
      const { index, episodeId } = event.detail;
      this.playEpisode(index, episodeId);
    },

    handleShowPlayer(event) {
      const playerData = event.detail;
      this.showPlayer(playerData);
    },

    handleHidePlayer() {
      this.hidePlayer();
    },
    
    showPlayer(playerData) {
      console.log('FloatingAudioPlayer receiving data:', playerData);

      // 更新音频管理器的状态
      audioManager.setAudioData(playerData.audioData);
      audioManager.setCurrentEpisodeIndex(playerData.currentEpisodeIndex);

      if (playerData.audioData.audioUrl) {
        audioManager.setAudioSource(playerData.audioData.audioUrl);
      }

      if (playerData.currentTime) {
        audioManager.setCurrentTime(playerData.currentTime);
      }

      this.visible = true;
      this.isMinimized = true;

      // 如果正在播放，继续播放
      if (playerData.isPlaying) {
        setTimeout(() => {
          audioManager.togglePlay();
        }, 100);
      }
    },

    hidePlayer() {
      this.visible = false;
    },

    toggleMinimize() {
      this.isMinimized = !this.isMinimized;
    },

    closeExpanded() {
      this.isMinimized = true;
    },

    closePlayer() {
      this.visible = false;
      // 暂停播放
      audioManager.togglePlay();
    },

    openFullPlayer() {
      // 通知主应用打开完整的音频播放器
      const audioState = audioManager.getState();
      const event = new CustomEvent('openAudioPlayer', {
        detail: {
          audioId: this.audioData.id,
          currentTime: audioState.currentTime,
          currentEpisodeIndex: audioState.currentEpisodeIndex
        }
      });
      window.dispatchEvent(event);
      this.hidePlayer();
    },

    togglePlay() {
      audioManager.togglePlay();

      // 同步到 AudioPlayer
      const event = new CustomEvent('syncFromFloatingPlayer', {
        detail: {
          type: this.isPlaying ? 'pause' : 'play'
        }
      });
      window.dispatchEvent(event);
    },

    playPrevious() {
      audioManager.playPrevious();
    },

    playNext() {
      audioManager.playNext();
    },

    seekTo(event) {
      const progressBar = this.$refs.progressBar;
      const rect = progressBar.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const percentage = (clickX / rect.width) * 100;

      audioManager.seekTo(percentage);

      // 同步到 AudioPlayer
      const syncEvent = new CustomEvent('syncFromFloatingPlayer', {
        detail: {
          type: 'seek',
          data: { percentage }
        }
      });
      window.dispatchEvent(syncEvent);
    },

    setVolume(event) {
      const volumeBar = this.$refs.volumeBar;
      const rect = volumeBar.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const volume = Math.max(0, Math.min(1, clickX / rect.width));

      audioManager.setVolume(volume);

      // 同步到 AudioPlayer
      const syncEvent = new CustomEvent('syncFromFloatingPlayer', {
        detail: {
          type: 'volume',
          data: { volume }
        }
      });
      window.dispatchEvent(syncEvent);
    },

    formatTime(seconds) {
      if (!seconds || isNaN(seconds)) return '00:00';
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    },

    async playEpisode(index, episodeId) {
      if (episodeId === undefined) {
        console.error('音频ID未定义，无法播放');
        return;
      }

      try {
        const episodeList = await getEpisodeList(episodeId, 720);
        const audioUrl = episodeList.data.playConfig.url;

        // 更新音频管理器的状态
        audioManager.setCurrentEpisodeIndex(index);
        audioManager.setAudioSource(audioUrl);

        // 更新本地音频数据
        this.audioData.audioUrl = audioUrl;

        // 播放音频
        setTimeout(() => {
          audioManager.togglePlay();
        }, 100);

        // 同步到 AudioPlayer
        const syncEvent = new CustomEvent('syncFromFloatingPlayer', {
          detail: {
            type: 'episode',
            data: { index, episodeId }
          }
        });
        window.dispatchEvent(syncEvent);
      } catch (error) {
        console.error('加载音频失败:', error);
      }
    },

    // 尝试恢复上次的播放会话
    async tryRestoreLastSession() {
      try {
        // 检查是否有保存的状态
        if (!audioManager.hasSavedState()) {
          console.log('没有找到保存的播放状态');
          return;
        }

        const savedAudioInfo = audioManager.getSavedAudioInfo();
        if (!savedAudioInfo) {
          console.log('保存的音频信息无效');
          return;
        }

        console.log('发现保存的播放状态，尝试恢复:', savedAudioInfo);

        // 恢复音频管理器状态
        const savedState = await audioManager.restoreSavedState();
        if (!savedState) {
          console.log('恢复状态失败');
          return;
        }

        // 需要重新获取完整的音频数据
        await this.restoreAudioData(savedAudioInfo, savedState);

      } catch (error) {
        console.error('恢复播放会话失败:', error);
        // 清除无效的保存状态
        audioManager.clearSavedState();
      }
    },

    // 恢复音频数据
    async restoreAudioData(savedAudioInfo, savedState) {
      try {
        // 这里需要根据保存的音频ID重新获取完整数据
        // 由于我们只保存了基本信息，需要重新调用API获取episodes等完整数据

        // 先设置基本的音频数据
        const basicAudioData = {
          id: savedAudioInfo.id,
          zhName: savedAudioInfo.zhName,
          artist: savedAudioInfo.artist,
          poster: savedAudioInfo.poster,
          audioUrl: savedAudioInfo.audioUrl,
          episodes: [] // 暂时为空，需要重新获取
        };

        // 更新音频管理器的数据
        audioManager.setAudioData(basicAudioData);
        audioManager.setCurrentEpisodeIndex(savedState.currentEpisodeIndex);

        // 如果有音频URL，设置音频源
        if (savedAudioInfo.audioUrl) {
          audioManager.setAudioSource(savedAudioInfo.audioUrl);

          // 恢复播放位置
          if (savedState.currentTime && savedState.currentTime !== '00:00') {
            // 等待音频加载后设置时间
            setTimeout(() => {
              const timeInSeconds = this.parseTimeToSeconds(savedState.currentTime);
              audioManager.setCurrentTime(timeInSeconds);
            }, 500);
          }
        }

        // 显示浮动播放器
        this.visible = true;
        this.isMinimized = true;

        console.log('成功恢复播放会话');

        // 发送事件通知其他组件
        const event = new CustomEvent('audioSessionRestored', {
          detail: {
            audioInfo: savedAudioInfo,
            savedState: savedState
          }
        });
        window.dispatchEvent(event);

      } catch (error) {
        console.error('恢复音频数据失败:', error);
        throw error;
      }
    },

    // 将时间字符串转换为秒数
    parseTimeToSeconds(timeString) {
      const parts = timeString.split(':');
      return parseInt(parts[0]) * 60 + parseInt(parts[1]);
    },

    // 手动清除保存的状态（可以在设置中调用）
    clearSavedSession() {
      audioManager.clearSavedState();
      console.log('已清除保存的播放会话');
    }
  }
};
</script>

<style scoped>
/* 背景遮罩样式 */
.player-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: transparent;
  z-index: 999;
  cursor: pointer;
}

.floating-audio-player {
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 320px;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(30px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  overflow: hidden;
  transform-origin: bottom right;
  transition: all 0.3s ease;
}

.floating-audio-player.minimized {
  width: 280px;
}

.floating-player-enter-active,
.floating-player-leave-active {
  transition: all 0.3s ease;
}

.floating-player-enter-from,
.floating-player-leave-to {
  opacity: 0;
  transform: translateY(100%) scale(0.8);
}

.player-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  transition: background-color 0.2s;
}

.player-header:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.track-info {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.track-cover-wrapper {
  position: relative;
  width: 40px;
  height: 40px;
  margin-right: 12px;
  transition: all 0.3s ease;
  animation: rotate 10s linear infinite;
  animation-play-state: paused;
}

.track-cover-wrapper.playing {
  animation-play-state: running;
}

.track-cover-wrapper:hover {
  transform: scale(1.05);
}

.track-cover {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.vinyl-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 12px;
  height: 12px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.track-details {
  flex: 1;
  min-width: 0;
}

.track-title {
  font-size: 14px;
  font-weight: 500;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
}

.track-artist {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.player-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.action-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.close-btn:hover {
  background: rgba(255, 0, 0, 0.1);
  color: #ff4444;
}

.player-content {
  padding: 16px;
}

.expanded-cover-section {
  display: flex;
  justify-content: center;
  margin-bottom: 15px;
}

.expanded-cover-wrapper {
  position: relative;
  width: 80px;
  height: 80px;
  transition: all 0.3s ease;
  animation: rotate 8s linear infinite;
  animation-play-state: paused;
}

.expanded-cover-wrapper.playing {
  animation-play-state: running;
}

.expanded-cover-wrapper:hover {
  transform: scale(1.1);
}

.expanded-cover {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}


.progress-section {
  margin-bottom: 16px;
}

.progress-bar {
  height: 4px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 2px;
  position: relative;
  cursor: pointer;
  margin-bottom: 8px;
}

.progress-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 2px;
}

.progress-current {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: #667eea;
  border-radius: 2px;
  transition: width 0.1s;
}

.time-info {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.8);
}

.control-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.control-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.control-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.control-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.play-btn-large {
  width: 44px;
  height: 44px;
  background: #667eea;
  color: #fff;
}

.play-btn-large:hover {
  background: #5a6fd8;
}

.volume-section {
  display: flex;
  align-items: center;
  gap: 10px;
}

.volume-bar {
  flex: 1;
  height: 3px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 1.5px;
  position: relative;
  cursor: pointer;
}

.volume-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 1.5px;
}

.volume-current {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: #667eea;
  border-radius: 1.5px;
}

/* 图标样式 */
.icon-play-small,
.icon-pause-small,
.icon-close,
.icon-prev-small,
.icon-next-small,
.icon-play-medium,
.icon-pause-medium,
.icon-expand,
.icon-volume-small {
  width: 16px;
  height: 16px;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

.icon-play-small {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23fff' viewBox='0 0 24 24'%3E%3Cpath d='M8 5v14l11-7z'/%3E%3C/svg%3E");
}

.icon-pause-small {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23fff' viewBox='0 0 24 24'%3E%3Cpath d='M6 19h4V5H6v14zm8-14v14h4V5h-4z'/%3E%3C/svg%3E");
}

.icon-close {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23fff' viewBox='0 0 24 24'%3E%3Cpath d='M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z'/%3E%3C/svg%3E");
}

.icon-prev-small {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23fff' viewBox='0 0 24 24'%3E%3Cpath d='M6 6h2v12H6V6zm3.5 6l8.5 6V6l-8.5 6z'/%3E%3C/svg%3E");
}

.icon-next-small {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23fff' viewBox='0 0 24 24'%3E%3Cpath d='M6 18l8.5-6L6 6v12zm10-12v12h2V6h-2z'/%3E%3C/svg%3E");
}

.icon-play-medium {
  width: 20px;
  height: 20px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23fff' viewBox='0 0 24 24'%3E%3Cpath d='M8 5v14l11-7z'/%3E%3C/svg%3E");
}

.icon-pause-medium {
  width: 20px;
  height: 20px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23fff' viewBox='0 0 24 24'%3E%3Cpath d='M6 19h4V5H6v14zm8-14v14h4V5h-4z'/%3E%3C/svg%3E");
}

.icon-expand {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23fff' viewBox='0 0 24 24'%3E%3Cpath d='M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z'/%3E%3C/svg%3E");
}

.icon-volume-small {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23fff' viewBox='0 0 24 24'%3E%3Cpath d='M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z'/%3E%3C/svg%3E");
}

/* 响应式设计 */
@media (max-width: 768px) {
  .floating-audio-player {
    bottom: 10px;
    right: 10px;
    width: 280px;
  }
  
  .floating-audio-player.minimized {
    width: 250px;
  }
  
  .expanded-cover-wrapper {
    width: 60px;
    height: 60px;
  }

}
</style>